import notification from "antd/es/notification";
import { call, put, select, takeEvery, takeLatest } from "redux-saga/effects";

import CumulAccess from "@mapmycustomers/shared/enum/cumul/CumulAccess";
import { CumulRole } from "@mapmycustomers/shared/enum/cumul/CumulRole";
import CumulShareType from "@mapmycustomers/shared/enum/cumul/CumulShareType";
import CumulAuth from "@mapmycustomers/shared/types/cumul/CumulAuth";
import CumulDashboard from "@mapmycustomers/shared/types/cumul/CumulDashboard";
import {
  CumulShareItem,
  CumulShareItemWithId,
} from "@mapmycustomers/shared/types/cumul/CumulShareItem";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { getShareItems, getUserCumulRole } from "@mapmycustomers/shared/util/cumul";

import i18nService from "@app/config/I18nService";
import { callApi } from "@app/store/api/callApi";
import { getCumulAuth } from "@app/store/cumul/selectors";
import { handleError } from "@app/store/errors/actions";
import { getCurrentUserId, getOrganizationId, getPinnedDashboardIds } from "@app/store/iam";
import { togglePinnedDashboard } from "@app/store/iam/actions";
import { getUsersOfEntireOrganization } from "@app/store/members";
import { getCaptchaToken } from "@app/util/captcha";

import {
  createDashboard,
  cumulDemoRequest,
  deleteDashboard,
  duplicateDashboard,
  fetchDashboardList,
  initializeCumul,
  saveSharing,
} from "./actions";

export function* onFetchDashboardList() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const cumulAuthenticationData: CumulAuth = yield select(getCumulAuth);

    if (cumulAuthenticationData?.token && cumulAuthenticationData?.id) {
      const payload = {
        cumulSSOKey: cumulAuthenticationData.id,
        cumulSSOToken: cumulAuthenticationData.token,
      };
      const dasboardList: ListResponse<CumulDashboard> = yield callApi(
        "fetchCumulDashboardList",
        orgId,
        payload,
        {
          $order: "-modified_at",
        }
      );
      yield put(fetchDashboardList.success(dasboardList.data));
    }
  } catch (error) {
    yield put(fetchDashboardList.failure(new Error("no token")));
    yield put(handleError({ error }));
  }
}

export function* onInitializeCumul() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const cumulAuthenticationResponse: CumulAuth = yield callApi("authorizeCumul", orgId);
    yield put(initializeCumul.success(cumulAuthenticationResponse));
    yield put(fetchDashboardList.request());
  } catch (error) {
    yield put(initializeCumul.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDeleteDashboard({ payload }: ReturnType<typeof deleteDashboard.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const cumulAuthenticationData: CumulAuth = yield select(getCumulAuth);
    const pinnedDashboardIds: CumulDashboard["id"][] = yield select(getPinnedDashboardIds);
    if (cumulAuthenticationData?.token && cumulAuthenticationData?.id) {
      const details = {
        cumulSSOKey: cumulAuthenticationData.id,
        cumulSSOToken: cumulAuthenticationData.token,
      };
      yield callApi("deleteDashboard", orgId, payload, details);
      yield put(fetchDashboardList.request());
    }
    yield put(deleteDashboard.success());
    if (pinnedDashboardIds.includes(payload)) {
      yield put(togglePinnedDashboard.request(payload));
    }
  } catch (error) {
    yield put(deleteDashboard.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onDuplicateDashboard({ payload }: ReturnType<typeof duplicateDashboard.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const cumulAuthenticationData: CumulAuth = yield select(getCumulAuth);
    const currentUserId: User["id"] = yield select(getCurrentUserId);
    if (cumulAuthenticationData?.token && cumulAuthenticationData?.id) {
      const details = {
        cumulSSOKey: cumulAuthenticationData.id,
        cumulSSOToken: cumulAuthenticationData.token,
        dashboardId: payload,
        userId: [currentUserId],
      };
      const duplicateDashboardData: CumulDashboard = yield callApi(
        "duplicateDashboard",
        orgId,
        payload,
        details
      );
      yield put(fetchDashboardList.request());
      yield put(duplicateDashboard.success(duplicateDashboardData));
    }
  } catch (error) {
    yield put(duplicateDashboard.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onCreateDashboard() {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const cumulAuthenticationData: CumulAuth = yield select(getCumulAuth);
    if (cumulAuthenticationData?.token && cumulAuthenticationData?.id) {
      const details = {
        cumulSSOKey: cumulAuthenticationData.id,
        cumulSSOToken: cumulAuthenticationData.token,
      };
      const newDashboardData: CumulDashboard = yield callApi("createDashboard", orgId, details);
      yield put(fetchDashboardList.request());
      yield put(createDashboard.success(newDashboardData));
    }
  } catch (error) {
    yield put(createDashboard.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onCumulDemoRequest({ payload }: ReturnType<typeof cumulDemoRequest.request>) {
  try {
    const token: string = yield call(getCaptchaToken, "cumuldemorequest");
    yield callApi("cumulDemoRequest", token, payload.message);

    const intl = i18nService.getIntl();
    if (intl) {
      notification.success({
        description: intl.formatMessage({
          id: "cumul.demo.request.description",
          defaultMessage:
            "A member of our team will be in touch within the next 1-2 business days with more information.",
          description: "Cumul demo requested success description",
        }),
        message: intl.formatMessage({
          id: "cumul.demo.request.header",
          defaultMessage: "Demo requested",
          description: "Cumul demo requested success message",
        }),
      });
    }
    yield put(cumulDemoRequest.success());
  } catch (error) {
    yield put(cumulDemoRequest.failure());
    yield put(handleError({ error }));
  }
}

function* updateSharingForType(
  shareType: CumulShareType,
  existingShareItems: CumulShareItem[],
  updatedShareItems: CumulShareItem[],
  requestDetails: {
    authPayload: {
      cumulSSOKey: string;
      cumulSSOToken: string;
    };
    dashboardId: CumulDashboard["id"];
    orgId: Organization["id"];
    users: User[];
  }
) {
  const { authPayload, dashboardId, orgId, users } = requestDetails;
  if (shareType === CumulShareType.ORG) {
    const hadOrg = existingShareItems.some((item) => item.type === shareType);
    const hasOrg = updatedShareItems.some((item) => item.type === shareType);

    const requestPayload = {
      ...authPayload,
      sharedTo: shareType,
      [shareType]: { access: CumulAccess.CAN_VIEW },
    };
    if (!hadOrg && hasOrg) {
      yield callApi("shareCumulDashboard", orgId, dashboardId, requestPayload);
    } else if (hadOrg && !hasOrg) {
      yield callApi("unshareCumulDashboard", orgId, dashboardId, requestPayload);
    }
    return;
  }

  const currentItems = existingShareItems.filter(
    (item) => item.type === shareType
  ) as CumulShareItemWithId[];
  const newShareItems = updatedShareItems.filter(
    (item) => item.type === shareType
  ) as CumulShareItemWithId[];

  const userIdToCumulAccess = new Map(
    users.map((user) => {
      return [
        user.id,
        getUserCumulRole(user) === CumulRole.DESIGNER ? CumulAccess.CAN_EDIT : CumulAccess.CAN_VIEW,
      ];
    })
  );

  const currentIdsSet = new Set(currentItems.map(({ id }) => id));
  const updatedIdsSet = new Set(newShareItems.map(({ id }) => id));

  const removedIds = currentItems.filter((item) => !updatedIdsSet.has(item.id));
  const addedIds = newShareItems.filter((item) => !currentIdsSet.has(item.id));

  if (addedIds.length) {
    const requestPayload = {
      ...authPayload,
      sharedTo: shareType,
      [shareType]: addedIds.map(({ id }) => ({
        id,
        access:
          shareType === CumulShareType.USER
            ? (userIdToCumulAccess.get(id) ?? CumulAccess.CAN_VIEW)
            : CumulAccess.CAN_VIEW,
      })),
    };
    yield callApi("shareCumulDashboard", orgId, dashboardId, requestPayload);
  }

  if (removedIds.length) {
    const requestPayload = {
      ...authPayload,
      sharedTo: shareType,
      [shareType]: removedIds.map(({ id }) => ({
        id,
        access:
          shareType === CumulShareType.USER
            ? (userIdToCumulAccess.get(id) ?? CumulAccess.CAN_VIEW)
            : CumulAccess.CAN_VIEW,
      })),
    };
    yield callApi("unshareCumulDashboard", orgId, dashboardId, requestPayload);
  }
}

function* onSaveSharing({ payload }: ReturnType<typeof saveSharing.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const users: User[] = yield select(getUsersOfEntireOrganization);

    const cumulAuthenticationData: CumulAuth = yield select(getCumulAuth);
    if (!cumulAuthenticationData?.token || !cumulAuthenticationData?.id) {
      yield put(saveSharing.failure());
      return;
    }
    const authPayload = {
      cumulSSOKey: cumulAuthenticationData.id,
      cumulSSOToken: cumulAuthenticationData.token,
    };

    const { dashboard, shareItems, successCallback } = payload;

    const currentShareItems = getShareItems(dashboard);
    yield call(updateSharingForType, CumulShareType.USER, currentShareItems, shareItems, {
      authPayload,
      dashboardId: dashboard.id,
      orgId,
      users,
    });
    yield call(updateSharingForType, CumulShareType.TEAM, currentShareItems, shareItems, {
      authPayload,
      dashboardId: dashboard.id,
      orgId,
      users,
    });
    yield call(updateSharingForType, CumulShareType.ORG, currentShareItems, shareItems, {
      authPayload,
      dashboardId: dashboard.id,
      orgId,
      users,
    });

    const dashboards: ListResponse<CumulDashboard> = yield callApi(
      "fetchCumulDashboardList",
      orgId,
      authPayload,
      {
        $limit: 1000,
      }
    );
    // fetchCumulDashboardList doesn't support filtering by id, and fetchCumulDashboard doesn't
    // return a board with groups and users, so we have to fetch all and then filter on the client side
    yield put(saveSharing.success(dashboards.data.find(({ id }) => id === dashboard.id)!));
    if (successCallback) {
      yield call(successCallback);
    }
  } catch (error) {
    yield put(saveSharing.failure());
    yield put(handleError({ error }));
  }
}

export function* cumulSaga() {
  yield takeEvery(initializeCumul.request, onInitializeCumul);
  yield takeLatest(fetchDashboardList.request, onFetchDashboardList);
  yield takeEvery(deleteDashboard.request, onDeleteDashboard);
  yield takeEvery(duplicateDashboard.request, onDuplicateDashboard);
  yield takeEvery(createDashboard.request, onCreateDashboard);
  yield takeEvery(cumulDemoRequest.request, onCumulDemoRequest);
  yield takeEvery(saveSharing.request, onSaveSharing);
}
